
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";

export interface BookingData {
  id: string;
  name: string;
  email: string;
  businessType: string;
  serviceType: "fixed" | "subscription";
  message: string;
  date: Date;
  timeSlot: string;
  status: "pending" | "confirmed" | "completed" | "cancelled";
  createdAt: Date;
}

// Type for booking input data (omitting the fields that will be generated)
export type BookingInput = Omit<BookingData, "id" | "status" | "createdAt">;

// Get all bookings from Supabase
export const getBookings = async (): Promise<BookingData[]> => {
  const { data, error } = await supabase
    .from('consultations')
    .select('*');

  if (error) {
    console.error("Failed to fetch bookings:", error);
    return [];
  }

  // Convert the data to match our BookingData interface
  return data.map((booking: any) => ({
    id: booking.id,
    name: booking.name,
    email: booking.email,
    businessType: booking.business_type,
    serviceType: booking.service_type as "fixed" | "subscription",
    message: booking.message,
    date: new Date(booking.date),
    timeSlot: booking.time_slot,
    status: booking.status as "pending" | "confirmed" | "completed" | "cancelled",
    createdAt: new Date(booking.created_at)
  }));
};

// Save a new booking to Supabase
export const saveBooking = async (bookingData: BookingInput): Promise<BookingData> => {
  // Transform data for Supabase table schema
  const supabaseData = {
    name: bookingData.name,
    email: bookingData.email,
    business_type: bookingData.businessType,
    service_type: bookingData.serviceType,
    message: bookingData.message,
    date: format(bookingData.date, "yyyy-MM-dd"),
    time_slot: bookingData.timeSlot,
    status: 'pending' as "pending" // Use type assertion to ensure correct status type
  };

  const { data, error } = await supabase
    .from('consultations')
    .insert(supabaseData)
    .select()
    .single();

  if (error) {
    console.error("Failed to save booking:", error);
    throw new Error(`Failed to save booking: ${error.message}`);
  }

  // Convert the response to match our BookingData interface
  return {
    id: data.id,
    name: data.name,
    email: data.email,
    businessType: data.business_type,
    serviceType: data.service_type as "fixed" | "subscription",
    message: data.message,
    date: new Date(data.date),
    timeSlot: data.time_slot,
    status: data.status as "pending" | "confirmed" | "completed" | "cancelled",
    createdAt: new Date(data.created_at)
  };
};

// Check if a time slot is available
export const isTimeSlotAvailable = async (date: Date, timeSlot: string): Promise<boolean> => {
  const dateStr = format(date, "yyyy-MM-dd");

  const { data, error } = await supabase
    .from('consultations')
    .select('*')
    .eq('date', dateStr)
    .eq('time_slot', timeSlot)
    .not('status', 'eq', 'cancelled');

  if (error) {
    console.error("Failed to check time slot availability:", error);
    return false;
  }

  return data.length === 0;
};

// Get available time slots for a specific date
export const getAvailableTimeSlots = async (date: Date, allTimeSlots: string[]): Promise<string[]> => {
  const dateStr = format(date, "yyyy-MM-dd");

  const { data, error } = await supabase
    .from('consultations')
    .select('time_slot')
    .eq('date', dateStr)
    .not('status', 'eq', 'cancelled');

  if (error) {
    console.error("Failed to get booked slots:", error);
    return allTimeSlots;
  }

  const bookedSlots = data.map((booking: any) => booking.time_slot);
  return allTimeSlots.filter(slot => !bookedSlots.includes(slot));
};

// Send email notification using secure Edge Function
// This keeps the Resend API key server-side only
export const sendEmailNotification = async (booking: BookingData): Promise<boolean> => {
  console.log("Sending email notification for booking via Edge Function");

  try {
    const { data, error } = await supabase.functions.invoke('send-email', {
      body: {
        type: 'booking_confirmation',
        booking: {
          name: booking.name,
          email: booking.email,
          businessType: booking.businessType,
          serviceType: booking.serviceType,
          date: format(booking.date, "MMMM d, yyyy"),
          timeSlot: booking.timeSlot,
          message: booking.message,
          status: booking.status,
        }
      }
    });

    if (error) {
      console.error("Edge Function error:", error);
      return false;
    }

    console.log("Email notification sent successfully");
    return data?.success || false;
  } catch (error) {
    console.error("Failed to send email notification:", error);
    return false;
  }
};

